import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useCards, useCreateCard } from '../../hooks/useCards'
import { useDeleteColumn, useUpdateColumn } from '../../hooks/useColumns'
import {
  CardDragItem,
  useDraggableColumn,
  useDroppableColumn,
} from '../../hooks/useDragAndDrop'
import { useOptimizedCardDrop } from '../../hooks/useOptimizedDragDrop'
import { ColumnResponse } from '../../types'
import { AddCardButton } from '../card/AddCardButton'
import { Card } from '../card/Card'
import { ConfirmDialog, Input, LoadingSpinner } from '../common'
import './Column.css'

interface ColumnProps {
  column: ColumnResponse
  boardId: number
  filterByDueDate?: string | null
}

/**
 * Column Component
 * Display a single column with its cards
 * Implements Requirements 4.2, 6.1, 6.2, 6.4, 6.3, 8.1, 8.2, 8.3
 */
export const Column: React.FC<ColumnProps> = React.memo(
  ({ column, boardId, filterByDueDate }) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { data: cards, isLoading } = useCards(column.id)
    const updateColumn = useUpdateColumn()
    const deleteColumn = useDeleteColumn()
    const createCard = useCreateCard(column.id)
    
    // OPTIMIZED: Use optimized drop handler for instant feedback
    const { handleCardDrop: optimizedCardDrop } = useOptimizedCardDrop(column.id)

    const [isEditing, setIsEditing] = useState(false)
    const [editedName, setEditedName] = useState(column.name)
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const [showMenu, setShowMenu] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)

    // OPTIMIZED: Sort cards by position - memoized to avoid re-sorting on every render
    const sortedCards = useMemo(
      () => {
        if (!cards) return []
        // Cards are already sorted from backend, but ensure consistency
        return [...cards].sort((a, b) => a.position - b.position)
      },
      [cards]
    )

    // OPTIMIZED: Filter cards by due date if filter is active
    const filteredCards = useMemo(() => {
      if (!filterByDueDate) return sortedCards
      return sortedCards.filter((card) => card.dueDate === filterByDueDate)
    }, [sortedCards, filterByDueDate])

    // OPTIMIZED: Use the optimized drop handler directly
    // Set up drop zone for the column
    const { isOver, canDrop, drop } = useDroppableColumn(
      column.id,
      optimizedCardDrop
    )

    // Set up drag for the column itself
    const {
      isDragging: isColumnDragging,
      drag: dragColumn,
      preview: previewColumn,
    } = useDraggableColumn({
      ...column,
      boardId: column.boardId || 0, // Ensure boardId is present
    })

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          menuRef.current &&
          !menuRef.current.contains(event.target as Node)
        ) {
          setShowMenu(false)
        }
      }

      if (showMenu) {
        document.addEventListener('mousedown', handleClickOutside)
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [showMenu])

    const handleRename = useCallback(async () => {
      if (!editedName.trim() || editedName === column.name) {
        setIsEditing(false)
        setEditedName(column.name)
        return
      }

      try {
        await updateColumn.mutateAsync({
          id: column.id,
          data: { name: editedName.trim() },
        })
        setIsEditing(false)
      } catch (error) {
        // Error is handled by the hook
        setEditedName(column.name)
      }
    }, [editedName, column.name, updateColumn])

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
          handleRename()
        } else if (e.key === 'Escape') {
          setIsEditing(false)
          setEditedName(column.name)
        }
      },
      [handleRename, column.name]
    )

    const handleDelete = useCallback(async () => {
      try {
        await deleteColumn.mutateAsync(column.id)
        setShowDeleteConfirm(false)
      } catch (error) {
        // Error is handled by the hook
      }
    }, [column.id, deleteColumn])

    const handleAddCard = useCallback(
      async (title: string) => {
        await createCard.mutateAsync({ title })
      },
      [createCard]
    )

    const handleCardClick = useCallback(
      (cardId: number) => {
        navigate(`/boards/${boardId}/cards/${cardId}`)
      },
      [navigate, boardId]
    )

    const handleMenuAction = useCallback((action: 'rename' | 'delete') => {
      setShowMenu(false)
      if (action === 'rename') {
        setIsEditing(true)
      } else if (action === 'delete') {
        setShowDeleteConfirm(true)
      }
    }, [])

    return (
      <section
        ref={(node) => {
          drop(node)
          previewColumn(node)
        }}
        className={`column ${isOver && canDrop ? 'column--drop-target' : ''} ${isColumnDragging ? 'column--dragging' : ''}`}
        aria-label={t('column.columnCards', { name: column.name })}
      >
        <div className="column__header">
          <div
            ref={dragColumn}
            className="column__drag-handle"
            title={t('column.dragToReorder')}
          >
            <span>⋮⋮</span>
          </div>
          {isEditing ? (
            <Input
              type="text"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              onBlur={handleRename}
              onKeyDown={handleKeyDown}
              autoFocus
              className="column__title-input"
            />
          ) : (
            <>
              <h3 className="column__title" onClick={() => setIsEditing(true)}>
                {column.name}
              </h3>
              <span className="column__count">{sortedCards.length}</span>
              <div className="column__menu" ref={menuRef}>
                <button
                  className="column__menu-button"
                  type="button"
                  aria-label={t('column.columnMenu')}
                  onClick={() => setShowMenu(!showMenu)}
                >
                  <span>⋮</span>
                </button>
                {showMenu && (
                  <div className="column__menu-dropdown">
                    <button
                      className="column__menu-item"
                      onClick={() => handleMenuAction('rename')}
                      type="button"
                    >
                      {t('column.rename')}
                    </button>
                    <button
                      className="column__menu-item column__menu-item--danger"
                      onClick={() => handleMenuAction('delete')}
                      type="button"
                    >
                      {t('column.delete')}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div
          className="column__content"
          role="list"
          aria-label={t('column.columnCards', { name: column.name })}
        >
          {isLoading ? (
            <div className="column__loading">
              <LoadingSpinner size="sm" />
            </div>
          ) : sortedCards.length === 0 ? (
            <div className="column__empty">
              <p>{t('column.noCards')}</p>
            </div>
          ) : (
            <div className="column__cards">
              {filteredCards.map((card) => (
                <div key={card.id} role="listitem">
                  <Card card={card} onClick={() => handleCardClick(card.id)} />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="column__footer">
          <AddCardButton columnId={column.id} onAddCard={handleAddCard} />
        </div>

        <ConfirmDialog
          isOpen={showDeleteConfirm}
          title={t('column.deleteColumn')}
          message={t('column.deleteColumnConfirm', {
            name: column.name,
            count: sortedCards.length,
          })}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
          confirmText={t('common.delete')}
          confirmVariant="danger"
        />
      </section>
    )
  }
)
